import React, { FC, HTMLAttributes, useRef } from 'react';
import cx from 'clsx';
import { useTranslation } from 'react-i18next';

import { useCart } from 'hooks/context/cart';
import { useGiftCardCart } from 'hooks/context/giftCardCart';
import { useMenu } from 'components/MenuContext/MenuContext';
import { useMerchant } from 'hooks/context/merchant';
import { useWindowSize } from 'hooks/breakpoints';

import { CloseButton } from 'components/CloseButton';
import { Icon } from 'components/Icon';
import { MerchantCurrency } from 'components/MerchantCurrency';
import { PlusTax } from 'components/PlusTax';
import { SmartLink } from 'components/SmartLink';
import { NoItemsCartWidget } from 'components/NoItemsCartWidget';

import { CART_WIDGET_TEST_IDS } from 'routes/Menu/MenuHeader/CartWidget/const';

import styles from './style.module.scss';
import { useCartPreview } from 'hooks/context/CartPreview';
import { useMerchantState } from 'hooks/context/MerchantState';
import { useHandleFocus } from 'hooks/useHandleFocus';
import { useServiceType } from 'hooks/context/serviceType';
import { fromCents } from 'util/Formatting';
import { handleKeyDownOnEnter, handleOnClick } from 'util/Dom';

type CartWidgetProps = HTMLAttributes<HTMLElement>;

export const CartWidget: FC<CartWidgetProps> = ({ className, ...props }) => {
  const { t } = useTranslation();
  const { isDesktop } = useWindowSize();
  const { itemCount: cartItemCount, subtotal: cartAmount } = useCart();
  const { itemCount: giftCardCount, subtotal: giftCardAmount } = useGiftCardCart();
  const { cartPreviewVisible, openCartPreview, closeCartPreview } = useCartPreview();
  const { isAcceptingOrders } = useMerchantState();
  const { tableDetails } = useServiceType();
  const itemCount = cartItemCount + giftCardCount;
  const tableOrderTotal = fromCents(tableDetails?.order?.total || 0.0);
  const tableOrderItemCount = tableDetails?.order?.cart?.items?.length ?? 0;
  const amount = cartAmount + giftCardAmount + tableOrderTotal;
  const containerRef = useRef<HTMLDivElement>(null);
  const showTaxContent = cartItemCount > 0 && tableOrderTotal === 0;

  const { isLoading: isMerchantLoading, merchant } = useMerchant();
  const validGiftCardOnlyOrder = cartItemCount === 0 && giftCardCount > 0 && merchant?.onlineServices?.GIFT_CARDS?.enabled;
  const { error: menuError } = useMenu();
  const notAcceptingOrders = !isAcceptingOrders || menuError || isMerchantLoading;

  const classNames = cx(styles.widget, {
    [styles.itemsInCart]: !!itemCount || !!tableOrderItemCount || validGiftCardOnlyOrder,
    [styles.disabled]: !!itemCount && notAcceptingOrders && !validGiftCardOnlyOrder,
    [styles.animation]: (!!itemCount && itemCount) <= 1 || !!tableOrderItemCount,
  }, className);

  // Bring focus into the cart for a11y
  useHandleFocus(containerRef, cartPreviewVisible);

  // Declare the content of the button here so we can conditionally render as a Link vs div
  const content = (
    <>
      <div className={styles.labelContainer} aria-hidden={!itemCount}>
        <span ref={containerRef} tabIndex={-1}>{t('cart.title')}</span>
        <div className={styles.cost}>
          <span data-testid={CART_WIDGET_TEST_IDS.cartPrice} className="bold">
            <MerchantCurrency amount={amount} />
          </span>
          <PlusTax locale={merchant?.locale} visible={showTaxContent} />
        </div>
      </div>
      <div className={styles.iconContainer}>
        <Icon aria-hidden={true} className={styles.icon} height={29} width={32} name="cart" />
        <div className={cx(styles.badge, 'p')} data-testid={CART_WIDGET_TEST_IDS.cartSize}>
          {itemCount}
        </div>
      </div>
      {cartPreviewVisible &&
        <div className={styles.closeContainer}>
          <CloseButton onClick={closeCartPreview} />
        </div>
      }
    </>
  );

  if ((!itemCount || notAcceptingOrders) && !validGiftCardOnlyOrder) {
    //if there's no item count but there are items from the tableOrder, it's a running order
    return <NoItemsCartWidget classNames={classNames} content={content} tableOrderItemCount={tableOrderItemCount} />;
  }

  if (!isDesktop) {
    return (
      <SmartLink to={'~cart'} className={classNames} data-testid={CART_WIDGET_TEST_IDS.cartPreview} {...props}>
        {content}
      </SmartLink>
    );
  }

  return (
    <div
        role={'button'}
        tabIndex={0}
        aria-expanded={cartPreviewVisible}
        onKeyDown={(key) => handleKeyDownOnEnter(key, openCartPreview)}
        className={classNames}
        data-testid={CART_WIDGET_TEST_IDS.cartPreview}
        {...props}
        onClick={(event) => handleOnClick(event, openCartPreview)} >
      {content}
    </div>
  );
};
